/**
 * PlayerCard — Rich inline player card for blog posts
 * Shows photo, name, position, value, trend with visual styling
 */
import { TrendUp, TrendDown } from "@phosphor-icons/react";
import { PlayerPhoto } from "@/components/PlayerPhoto";
import { Link } from "react-router-dom";

const POS_COLORS = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24" };

export function PlayerCard({ name, position, team, value, trend, sleeperId, verdict, size = "default" }) {
  const slug = name?.toLowerCase().replace(/['.]/g, "").replace(/\s+/g, "-");
  const color = POS_COLORS[position] || "#888";
  const isCompact = size === "compact";

  return (
    <Link
      to={`/players/${slug}`}
      className={`flex items-center gap-3 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline transition-colors rounded-sm ${isCompact ? "p-2.5" : "p-3 sm:p-4"}`}
      data-testid={`player-card-${slug}`}
    >
      <PlayerPhoto playerId={sleeperId} name={name} position={position} size={isCompact ? 36 : 44} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`${isCompact ? "text-[13px]" : "text-sm"} font-bold text-white`}>{name}</span>
          {verdict && (
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${
              verdict === "BUY" ? "bg-[#4ecdc4]/15 text-[#4ecdc4]" :
              verdict === "SELL" ? "bg-[#ff6b6b]/15 text-[#ff6b6b]" :
              "bg-[#fbbf24]/15 text-[#fbbf24]"
            }`}>{verdict}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm" style={{ color, backgroundColor: `${color}15` }}>{position}</span>
          <span className="text-[10px] text-[#666]">{team}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        {value > 0 && <p className="font-mono font-bold text-[13px] text-[#ccff00]">{value.toLocaleString()}</p>}
        {trend != null && trend !== 0 && (
          <div className={`flex items-center justify-end gap-0.5 ${trend > 0 ? "text-[#4ade80]" : "text-[#ff3b30]"}`}>
            {trend > 0 ? <TrendUp size={10} weight="bold" /> : <TrendDown size={10} weight="bold" />}
            <span className="text-[10px] font-mono font-bold">{trend > 0 ? "+" : ""}{trend}</span>
          </div>
        )}
      </div>
    </Link>
  );
}

export function PlayerCardGrid({ players, verdict, columns = 1 }) {
  if (!players?.length) return null;
  return (
    <div className={`grid gap-2 my-4 ${columns === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
      {players.map((p, i) => (
        <PlayerCard
          key={i}
          name={p.name}
          position={p.position}
          team={p.team}
          value={p.value}
          trend={p.trend}
          sleeperId={p.sleeperId}
          verdict={verdict}
          size={columns === 2 ? "compact" : "default"}
        />
      ))}
    </div>
  );
}
